import { useCallback, useRef, useEffect } from 'react';
import CreateApi from '../../shared/Settings/appSettings';
import { useTBContext } from '../Contexts/useTBStates';
import type { PaceOfPlayRow } from '@moment-golf/types';

function teeTimeMs(val: string | undefined | null): number {
    if (!val) return 0;
    const d = new Date(val);
    return isNaN(d.getTime()) ? 0 : d.getTime();
}

export function useFetchPaceData() {
    const api = CreateApi();
    const {
        viewEvent, roundValue,
        paceData, setPaceData,
        paceDataLastFetched, setPaceDataLastFetched,
    } = useTBContext();

    const paceDataRef = useRef<PaceOfPlayRow[]>(paceData);
    const lastFetchedRef = useRef<string | null>(paceDataLastFetched);

    useEffect(() => { paceDataRef.current = paceData; }, [paceData]);
    useEffect(() => { lastFetchedRef.current = paceDataLastFetched; }, [paceDataLastFetched]);

    const fetchPaceData = useCallback(async (full = false) => {
        if (!viewEvent || !(viewEvent as any)._id) return;

        const eventID = (viewEvent as any)._id as string;
        const since = full ? undefined : lastFetchedRef.current ?? undefined;

        const params: Record<string, any> = {
            eventID,
            roundNumber: roundValue,
        };
        if (since) params.since = since;

        const res = await api.get('/tb/GetPaceOfPlay', { params });
        const incoming: PaceOfPlayRow[] = res.data;

        if (incoming.length === 0 && !full) {
            return;
        }

        if (full || paceDataRef.current.length === 0) {
            setPaceData(incoming);
        } else {
            const merged = [...paceDataRef.current];
            incoming.forEach((row) => {
                const idx = merged.findIndex(
                    (r) => teeTimeMs(r.teeTime) === teeTimeMs(row.teeTime),
                );
                if (idx !== -1) {
                    merged[idx] = row;
                } else {
                    merged.push(row);
                }
            });
            setPaceData(merged);
        }

        if (incoming.length > 0) {
            const latestModified = incoming.reduce((max, row) => {
                const t = new Date(row.modifiedAt).getTime();
                return t > max ? t : max;
            }, 0);
            setPaceDataLastFetched(new Date(latestModified).toISOString());
        }
    }, [viewEvent, roundValue, api, setPaceData, setPaceDataLastFetched]);

    return { fetchPaceData };
}

export { teeTimeMs };
